import {
  AfterViewInit,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  model,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { ImageCroppedEvent, ImageCropperComponent } from 'ngx-image-cropper';
import SignaturePad, { BasicPoint } from 'signature_pad';

import {
  CipoSignature,
  DEFAULT_SIGNATURE_OPTIONS,
  DEFAULT_SIGNATURE_POINT,
  SignatureType,
} from '@module-definition/signature/common';
import { MainService } from '@module-instance/services';
import { CipoCheckboxComponent } from '@shared/components/fields/cipo-checkbox/cipo-checkbox.component';
import { CipoCheckboxControl } from '@shared/components/fields/common';
import { CipoFormModule } from '@shared/modules';
import { NotificationService } from '@shared/services';

@Component({
  selector: 'cipo-signature',
  imports: [CipoFormModule, MatButtonToggleModule, ImageCropperComponent, CipoCheckboxComponent],
  templateUrl: './signature.component.html',
  styleUrl: './signature.component.scss',
})
export class SignatureComponent implements AfterViewInit {
  private readonly mainService = inject(MainService);
  private readonly notify = inject(NotificationService);

  /** Expose enum to template */
  readonly SignatureType = SignatureType;

  currentSignature = model<CipoSignature>();

  canvas = viewChild<ElementRef<HTMLCanvasElement>>('canvas');
  fileInput = viewChild<ElementRef<HTMLInputElement>>('fileInput');

  signaturePad!: SignaturePad;

  text = signal('');
  /** Current active signature input mode */
  mode = signal<SignatureType>(SignatureType.Draw);
  padEmpty = signal(true);
  agreementControl = new CipoCheckboxControl(false, {
    label: 'signature.signAgreement',
    required: true,
    testId: 'sign-agreement-checkbox',
  });
  agree = toSignal(this.agreementControl.valueChanges, { initialValue: this.agreementControl.value });

  /** Base64 image file event from the file input, fed into ngx-image-cropper */
  imageChangedEvent = signal<Event>(null);
  /** Cropped image base64 output (no data: prefix) */
  croppedImageBase64 = signal<string>('');

  /* Get the font size based on the input text length */
  inputFontSize = computed(() => {
    const textLength = this.text().length;
    if (textLength > 21) {
      return 20; // Minimum font size
    }
    const breakpoint = Math.floor(textLength / 5) + 1;
    return 60 - breakpoint * 8;
  });

  disabledSave = computed(() => {
    if (!this.agree()) {
      return true;
    }
    switch (this.mode()) {
      case SignatureType.Draw:
        return this.padEmpty();
      case SignatureType.Text:
        return this.text().trim().length === 0;
      case SignatureType.Image:
        return this.croppedImageBase64().length === 0;
    }
  });

  constructor() {
    effect(() => {
      const signature = this.currentSignature();
      if (!signature || !this.signaturePad) {
        return;
      }
      untracked(() => this.populatePad(signature));
    });
  }

  ngAfterViewInit() {
    this.signaturePad = new SignaturePad(this.canvas().nativeElement);
    if (this.currentSignature()) {
      this.populatePad(this.currentSignature());
      this.agreementControl.setValue(true);
      this.agreementControl.disable();
    }
  }

  populatePad(signature: CipoSignature) {
    this.signaturePad.clear();
    this.text.set(signature.signatureText || '');
    this.padEmpty.set(!signature.signatureText || signature.signatureText.trim().length === 0);

    if (signature.type === SignatureType.Draw) {
      this.mode.set(SignatureType.Draw);
      const { compositeOperation, dotSize, maxWidth, minWidth, penColor, velocityFilterWeight } =
        DEFAULT_SIGNATURE_OPTIONS;
      const { time: defaultTime, pressure } = DEFAULT_SIGNATURE_POINT;
      const pointsArray = signature.signatureText
        .split(';/')
        .filter(part => !!part.length)
        .map(pointsGroup =>
          pointsGroup.split(';').map((point, index) => {
            const [x, y, savedTime] = point.split(',').map(Number);
            // If savedTime is not provided, use defaultTime and increment it based on index (for old signatures)
            const time = savedTime || defaultTime + index * 10;
            return { x, y, time, pressure } as BasicPoint;
          }),
        );

      const pointGroup = pointsArray.map(points => ({
        points,
        velocityFilterWeight,
        dotSize,
        minWidth,
        maxWidth,
        penColor,
        compositeOperation,
      }));
      this.signaturePad.fromData(pointGroup);
    } else if (signature.type === SignatureType.Image) {
      this.mode.set(SignatureType.Image);
      this.croppedImageBase64.set(signature.signature || '');
    } else {
      this.mode.set(SignatureType.Text);
    }
  }

  clearPad() {
    this.signaturePad.clear();
    this.text.set('');
    this.padEmpty.set(true);
    this.imageChangedEvent.set(null);
    this.croppedImageBase64.set('');
    // Reset the file input value so the same image can be re-uploaded after clearing
    const fileInput = this.fileInput();
    if (fileInput) {
      fileInput.nativeElement.value = '';
    }
  }

  /** Called by the file input when the user selects an image to upload */
  onFileSelected(event: Event) {
    this.imageChangedEvent.set(event);
    this.croppedImageBase64.set('');
  }

  /** Called by ngx-image-cropper whenever the user adjusts the crop area */
  onImageCropped(event: ImageCroppedEvent) {
    // Strip the data:image/...;base64, prefix to keep consistent with the other signature types
    const base64 = event.base64 || '';
    this.croppedImageBase64.set(base64.includes('base64,') ? base64.split('base64,')[1] : base64);
  }

  savePad() {
    if (this.mode() === SignatureType.Image) {
      this.currentSignature.update(value => ({
        ...value,
        signature: this.croppedImageBase64(),
        fontSize: 0,
        signatureText: '',
        type: SignatureType.Image,
      }));
    } else if (this.mode() === SignatureType.Text) {
      const canvas = this.canvas().nativeElement;
      const ctx = canvas.getContext('2d');
      const text = this.text().trim();

      // Clear previous content
      this.signaturePad.clear();

      // Make sure the font is loaded
      ctx.font = `${this.inputFontSize()}px 'La Belle Aurore'`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillStyle = '#000'; // Black ink color

      // Draw text in center of canvas
      ctx.fillText(text, canvas.width / 2, canvas.height / 2);

      const base64Data = this.signaturePad.toDataURL();
      this.currentSignature.update(value => ({
        ...value,
        signature: base64Data.split('base64,')[1],
        fontSize: this.inputFontSize(),
        signatureText: this.text(),
        type: SignatureType.Text,
      }));
    } else {
      const pointsArray = this.signaturePad.toData().map(pointGroup => pointGroup.points);
      const pointsText = pointsArray
        .map(pointsGroup =>
          pointsGroup
            .map(({ x, y, time }) => {
              // get only last 5 digits of time - this means that we save up to 99999 ms (100 seconds) for one signature stroke
              const timeStr = time.toString().slice(-5);
              return `${x.toFixed(2)},${y.toFixed(2)},${timeStr}`;
            })
            .join(';'),
        )
        .join(';/');
      this.text.set(`${pointsText};/`);
      this.padEmpty.set(false);

      const base64Data = this.signaturePad.toDataURL();
      this.currentSignature.update(value => ({
        ...value,
        signature: base64Data.split('base64,')[1],
        fontSize: this.inputFontSize(),
        signatureText: this.text(),
        type: SignatureType.Draw,
      }));
    }

    this.mainService
      .createSignature(this.currentSignature())
      .subscribe(() => this.notify.success('signature.updatedSuccessfully'));
  }
}
